/**
 * frontend/src/pages/MyPlaylists.jsx — /playlists
 * SoundDAO Sprint 6A — "Built here" (Archive playlists) + "Linked" (the external-URL
 * submission, moved here unchanged from the Player dashboard).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createPlaylist, myPlaylists } from '../lib/archiveApi';
import { track } from '../lib/analytics';
import { PLAYLIST_SHARE_MIN_TRACKS as SHARE_MIN } from '../lib/constants';
import LinkedPlaylists from '../components/LinkedPlaylists';
import '../styles/archive.css';

const COVERS = ['linear-gradient(135deg,#667eea,#764ba2)', 'linear-gradient(135deg,#f6d365,#fda085)', 'linear-gradient(135deg,#43e97b,#38f9d7)', 'linear-gradient(135deg,#f093fb,#f5576c)'];

function artistsLine(p) {
  const names = [];
  for (const t of p.tracks || []) for (const a of t.artists || []) if (!names.includes(a.displayName)) names.push(a.displayName);
  if (!names.length) return '';
  return names.length > 3 ? `${names.slice(0, 3).join(', ')} +${names.length - 3}` : names.join(', ');
}

export default function MyPlaylists({ session, user }) {
  const navigate = useNavigate();
  const userId = session?.user?.id || user?.id;
  const [lists, setLists] = useState(null);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setError(null);
    myPlaylists().then((d) => setLists(d.playlists || [])).catch((e) => { setLists([]); setError(e.message); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    const title = name.trim();
    if (!title || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { playlist } = await createPlaylist({ title });
      track('playlist_built');
      navigate(`/playlists/${playlist.id}`);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="ax-page">
      <div className="ax-wrap ax-two">
        <div>
          <h1 className="ax-h1">My Playlists</h1>
          <p className="ax-sub">Build from the Archive, or share a playlist you made elsewhere.</p>

          <h2 className="ax-h2">Built here</h2>
          {error && <div className="ax-banner ax-banner--error"><span>{error}</span><button type="button" className="ax-btn ax-btn--sm" onClick={load}>Retry</button></div>}
          {lists === null && <><div className="ax-skel" /><div className="ax-skel" /></>}
          {lists && lists.length === 0 && !error && (
            <div className="ax-card ax-meta">No playlists yet. Dig the Archive, tap “+ Playlist” on any track, and your first one is made.</div>
          )}
          {(lists || []).map((p, i) => {
            const need = SHARE_MIN - p.trackCount;
            const status = [
              `${p.trackCount} track${p.trackCount === 1 ? '' : 's'}`,
              p.isPublic ? 'Public' : 'Private',
              need > 0 ? `add ${need} more to share` : artistsLine(p),
            ].filter(Boolean).join(' · ');
            return (
              <Link key={p.id} to={`/playlists/${p.id}`} className="ax-card ax-card-link">
                <div className="ax-cover" style={{ background: COVERS[i % COVERS.length] }} aria-hidden="true">🎸</div>
                <div className="ax-grow">
                  <div className="ax-title">{p.title}</div>
                  <div className="ax-meta">{status}</div>
                </div>
                <span aria-hidden="true">›</span>
              </Link>
            );
          })}

          {creating ? (
            <form className="ax-search" onSubmit={create} style={{ marginTop: 4 }}>
              <input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="Playlist name…" aria-label="Playlist name" />
              <button type="submit" className="ax-btn ax-btn--sm ax-btn--primary" disabled={!name.trim() || busy}>Create</button>
              <button type="button" className="ax-btn ax-btn--sm" onClick={() => { setCreating(false); setName(''); }}>Cancel</button>
            </form>
          ) : (
            <div className="ax-actions">
              <button type="button" className="ax-btn ax-btn--primary" onClick={() => setCreating(true)}>+ New playlist</button>
              <Link to="/dig" className="ax-btn">🔍 Dig the Archive</Link>
            </div>
          )}
        </div>

        <div>
          <h2 className="ax-h2">Linked</h2>
          <div className="ax-linked">
            <LinkedPlaylists userId={userId} />
          </div>
        </div>
      </div>
    </div>
  );
}
